import { Loader2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import {
  DELETE_PATIENT_DATA_LABEL,
  DELETE_PATIENT_DATA_PATH,
  PRIVACY_EMAIL,
} from "@/frontend/constants/consent";
import { DELETE_PATIENT_DATA_GRACE_DAYS_MAX } from "@/server/constants/delete-patient-data";
import type { MedicareRecipient } from "@/frontend/types/consent";

type MedicareConsentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Who receives the Medicare data, which decides the whole disclosure. */
  recipient: MedicareRecipient;
  /** Proceeds to the Medicare sign-in. */
  onConfirm: () => void;
  /** True while the handoff is starting, or when it cannot start yet. */
  isBusy?: boolean;
};

/**
 * The Medicare disclosure CMS Blue Button 3b.2 requires before a patient signs
 * in at Medicare, naming every party that ends up with the data. Every surface
 * that starts a Medicare connection shows this one, so the required text cannot
 * drift between them.
 */
export function MedicareConsentDialog({
  open,
  onOpenChange,
  recipient,
  onConfirm,
  isBusy = false,
}: MedicareConsentDialogProps) {
  const ownRecords = recipient.kind === "self";
  // Who the disclosure addresses the data as going to. The own-records reader is
  // the patient, so it is theirs and Medblocks is the only other party.
  const recipientName = ownRecords
    ? "Medblocks"
    : recipient.kind === "medblocks"
      ? "Medblocks"
      : recipient.organizationName || "the organization that invited you";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {ownRecords ? (
              <>Share your Medicare data with Medblocks</>
            ) : (
              <>
                Share your Medicare data with <span data-patient>{recipientName}</span>
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            You&apos;ll sign in at Medicare. Your data then goes to{" "}
            <span data-patient>{recipientName}</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-medium mb-1">Who gets your data</p>
            {ownRecords ? (
              <p className="text-muted-foreground">
                Medblocks retrieves and stores your Medicare data so you can see it alongside
                your other connected health data. It is not shared with anyone else. Medblocks
                is HIPAA and SOC 2 Type II compliant, and doesn&apos;t sell your data.
              </p>
            ) : recipient.kind === "medblocks" ? (
              <p className="text-muted-foreground">
                Medblocks retrieves and stores your Medicare data, and nothing else. Anyone at Medblocks given access to your record can see it, not only the person who invited you. Medblocks is HIPAA and SOC 2 Type II compliant, and doesn&apos;t sell your data or share it with anyone else.
              </p>
            ) : (
              <ul className="list-disc pl-5 text-muted-foreground space-y-1.5">
                <li>
                  <span className="font-medium text-foreground">Medblocks</span>, the platform <span data-patient>{recipientName}</span> uses to retrieve and store your Medicare data. Medblocks is HIPAA and SOC 2 Type II compliant, and doesn&apos;t sell your data to anyone. The data is shared only to <span data-patient>{recipientName}</span>. 
                </li>
                <li>
                  <span data-patient className="font-medium text-foreground">{recipientName}</span>, the organization that invited you, can see your Medicare data. Anyone <span data-patient>{recipientName}</span> has given access to your record can see it too, not only the person who invited you.
                </li>
              </ul>
            )}
          </div>
          <div>
            <p className="font-medium mb-1">What we&apos;ll access</p>
            <ul className="list-disc pl-5 text-muted-foreground space-y-0.5">
              <li>Your Medicare profile (name, date of birth, Medicare ID)</li>
              <li>Your Medicare Part A, B, C, and D coverage</li>
              <li>Your claims history (Explanation of Benefits)</li>
            </ul>
          </div>
          <div>
            <p className="font-medium mb-1">Why</p>
            <p className="text-muted-foreground">
              {ownRecords ? (
                <>
                  So you can see your Medicare record alongside your other connected health
                  data.
                </>
              ) : (
                <>
                  So <span data-patient>{recipientName}</span> can see your Medicare record alongside your other connected health data for care coordination.
                </>
              )}
            </p>
          </div>
          <div>
            <p className="font-medium mb-1">You stay in control</p>
            <p className="text-muted-foreground">
              To unlink Medicare, use the{" "}
              <a
                href={DELETE_PATIENT_DATA_PATH}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                {DELETE_PATIENT_DATA_LABEL}
              </a>{" "}
              page or email{" "}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="underline">{PRIVACY_EMAIL}</a>
              {" "}and we&apos;ll delete your Medicare data within{" "}
              {DELETE_PATIENT_DATA_GRACE_DAYS_MAX} days. You can also revoke access directly at{" "}
              <a href="https://www.medicare.gov/account/login" target="_blank" rel="noopener noreferrer" className="underline">
                medicare.gov
              </a>.
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isBusy}>
            {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Continue to Medicare
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
